import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import Select from 'react-select';
import { FaBook, FaSearch, FaFilePdf, FaFileExcel, FaFileInvoice, FaMoneyCheck, FaUndoAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { clientActions } from '../../sagas/clientSlice';
import invoiceService from '../../services/invoiceService';
import * as reglementService from '../../services/reglementService';
import customerReturnService from '../../services/customerReturnService';
import { formatPrice } from '../../helpers/utility';

const pad = (n) => String(n).padStart(2, '0');
const formatDateInput = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const ddmmyyyy = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateFr = (value) => {
  const date = parseDate(value);
  return date ? date.toLocaleDateString('fr-FR') : '-';
};

const formatMonthKey = (value) => {
  const date = parseDate(value);
  if (!date) return 'Sans date';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey || monthKey === 'Sans date') return 'Sans date';
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
  });
};

const getMonthRange = (monthValue) => {
  if (!monthValue) return { dateFrom: '', dateTo: '' };
  const [year, month] = monthValue.split('-').map(Number);
  if (!year || !month) return { dateFrom: '', dateTo: '' };

  return {
    dateFrom: formatDateInput(new Date(year, month - 1, 1)),
    dateTo: formatDateInput(new Date(year, month, 0)),
  };
};

const normalizeNumber = (value) => Number(value || 0);
const splitDocumentNumbers = (value) => (value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const formatSignedBalance = (value) => {
  const amount = normalizeNumber(value);
  const formatted = formatPrice(Math.abs(amount), false);
  if (amount > 0) return `+${formatted}`;
  if (amount < 0) return `-${formatted}`;
  return formatted;
};

const balanceTextClass = (value) => {
  const amount = normalizeNumber(value);
  if (amount > 0) return 'text-success';
  if (amount < 0) return 'text-danger';
  return 'text-muted';
};

const getTypeIcon = (type) => {
  if (type === 'REGLEMENT') return FaMoneyCheck;
  if (type === 'RETOUR') return FaUndoAlt;
  return FaFileInvoice;
};

const GrandLivreClientPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const clients = useSelector((state) => state.clients.clients || []);
  const { company } = useSelector((state) => state.auth);

  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

  const [filters, setFilters] = useState({
    clientId: null,
    month: `${today.getFullYear()}-${pad(today.getMonth() + 1)}`,
    dateFrom: formatDateInput(startOfMonth),
    dateTo: formatDateInput(tomorrow),
    query: '',
  });
  const [rows, setRows] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clients.length) {
      dispatch(clientActions.search({ query: '' }));
    }
  }, [clients.length, dispatch]);

  const clientOptions = useMemo(() => {
    const mapped = clients.map((client) => ({
      value: client.id,
      label: client.typeClient === 'PARTICULIER'
        ? `${client.nom || ''} ${client.prenom || ''}`.trim()
        : (client.entreprise || client.nom || '-'),
    }));

    return [{ value: null, label: t('common.all', 'Tous') }, ...mapped];
  }, [clients, t]);

  const fetchLedger = async () => {
    setLoading(true);
    setError(null);

    try {
      const [invoiceResult, reglementResult, returnResult] = await Promise.all([
        invoiceService.listWithSubInvoices({
          clientId: filters.clientId,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          query: filters.query,
        }),
        reglementService.rechercherReglements({
          clientId: filters.clientId,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          query: filters.query,
        }),
        customerReturnService.list({
          clientId: filters.clientId,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          query: filters.query,
        }),
      ]);

      const invoiceRows = (invoiceResult?.body || []).map((invoice) => ({
        id: `invoice-${invoice.id}`,
        date: invoice.dateFacture,
        type: 'FACTURE',
        icon: getTypeIcon('FACTURE'),
        numero: invoice.numero || '-',
        libelle: [
          t('nav.invoice', 'Facture'),
          splitDocumentNumbers(invoice.paymentsRefs).length
            ? `Paiements: ${splitDocumentNumbers(invoice.paymentsRefs).join(', ')}`
            : null,
        ].filter(Boolean).join(' - '),
        client: invoice.clientNom || '-',
        debit: normalizeNumber(invoice.montantTotalTTC),
        credit: 0,
        raw: invoice,
      }));

      const reglementRows = (reglementResult?.data?.body || reglementResult?.data || []).map((reglement) => ({
        id: `reglement-${reglement.id}`,
        date: reglement.dateReglement,
        type: 'REGLEMENT',
        icon: getTypeIcon('REGLEMENT'),
        numero: reglement.reference || reglement.id || '-',
        libelle: [
          getModeLabel(reglement.modeReglement),
          splitDocumentNumbers(reglement.numFactures).length
            ? `Factures: ${splitDocumentNumbers(reglement.numFactures).join(', ')}`
            : null,
          reglement.modeReglement === 'CHEQUE'
            ? [
              reglement.numeroCheque ? `Cheque N ${reglement.numeroCheque}` : null,
              reglement.dateEcheance ? `Ech. ${formatDateFr(reglement.dateEcheance)}` : null,
            ].filter(Boolean).join(' - ')
            : null,
          reglement.modeReglement !== 'CHEQUE' && reglement.dateEcheance
            ? `Ech. ${formatDateFr(reglement.dateEcheance)}`
            : null,
        ].filter(Boolean).join(' - '),
        client: reglement.nomClient || reglement.clientNom || '-',
        debit: 0,
        credit: normalizeNumber(reglement.montantRegle),
        raw: reglement,
      }));

      const returnRows = (returnResult?.data?.body || returnResult?.data || []).map((retour) => ({
        id: `retour-${retour.id}`,
        date: retour.dateOperation,
        type: 'RETOUR',
        icon: getTypeIcon('RETOUR'),
        numero: retour.numeroRetour || retour.id || '-',
        libelle: [
          t('nav.refund', 'Retour client'),
          retour.modeRemboursement || null,
          retour.motifRetour || null,
        ].filter(Boolean).join(' - '),
        client: retour.clientNom || '-',
        debit: 0,
        credit: normalizeNumber(retour.montantTTC),
        raw: retour,
      }));

      let runningBalance = 0;
      const merged = [...invoiceRows, ...reglementRows, ...returnRows]
        .sort((a, b) => {
          const aDate = parseDate(a.date)?.getTime() || 0;
          const bDate = parseDate(b.date)?.getTime() || 0;
          if (aDate !== bDate) return aDate - bDate;
          return String(a.numero).localeCompare(String(b.numero));
        })
        .map((row) => {
          const balanceBefore = runningBalance;
          runningBalance += row.credit - row.debit;
          return {
            ...row,
            balanceBefore,
            balance: runningBalance,
          };
        });

      setOpeningBalance(0);
      setRows(merged);
    } catch (err) {
      console.error('Error loading client ledger:', err);
      setError(err?.message || t('common.error', 'Une erreur est survenue'));
      setRows([]);
      setOpeningBalance(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const totalDebit = rows.reduce((sum, row) => sum + row.debit, 0);
    const totalCredit = rows.reduce((sum, row) => sum + row.credit, 0);
    const balance = rows.length ? rows[rows.length - 1].balance : openingBalance;

    return { totalDebit, totalCredit, balance };
  }, [openingBalance, rows]);

  const monthlySections = useMemo(() => {
    const grouped = new Map();

    rows.forEach((row) => {
      const monthKey = formatMonthKey(row.date);
      const existing = grouped.get(monthKey) || {
        monthKey,
        monthLabel: formatMonthLabel(monthKey),
        openingBalance: row.balanceBefore,
        rows: [],
        totalDebit: 0,
        totalCredit: 0,
        closingBalance: row.balance,
      };

      existing.rows.push(row);
      existing.totalDebit += row.debit;
      existing.totalCredit += row.credit;
      existing.closingBalance = row.balance;
      grouped.set(monthKey, existing);
    });

    return Array.from(grouped.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [rows]);

  function getModeLabel(mode) {
    switch (mode) {
      case 'ESPECES':
        return t('facture.modeReglement.espece', 'Especes');
      case 'CHEQUE':
        return t('facture.modeReglement.cheque', 'Cheque');
      case 'VIREMENT':
        return t('facture.modeReglement.virement', 'Virement');
      case 'TRAITE':
        return t('facture.modeReglement.traite', 'Traite');
      case 'CARTE_BANCAIRE':
        return t('facture.modeReglement.card', 'Carte bancaire');
      default:
        return mode || '-';
    }
  }

  const typeLabel = (type) => {
    switch (type) {
      case 'FACTURE':
        return t('nav.invoice', 'Facture client');
      case 'REGLEMENT':
        return t('nav.listeReglements', 'Reglement client');
      case 'RETOUR':
        return t('nav.refund', 'Retour client');
      default:
        return type || '-';
    }
  };

  const selectedClientLabel = useMemo(() => {
    if (!filters.clientId) return t('common.all', 'Tous');
    return clientOptions.find((option) => option.value === filters.clientId)?.label || '-';
  }, [clientOptions, filters.clientId, t]);

  const handleExportExcel = () => {
    if (!rows.length) return;

    const data = rows.map((row) => ({
      Date: formatDateFr(row.date),
      Client: row.client,
      Type: typeLabel(row.type),
      Reference: row.numero,
      Libelle: row.libelle,
      Debit: row.debit || 0,
      Credit: row.credit || 0,
      Solde: row.balance || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = [
      { wch: 14 },
      { wch: 28 },
      { wch: 18 },
      { wch: 20 },
      { wch: 42 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'GrandLivreClients');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    saveAs(blob, `Grand_Livre_Clients_${filters.dateFrom}_${filters.dateTo}.xlsx`);
  };

  const handleExportPdf = () => {
    if (!rows.length) return;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(t('sales.grandLivreClient', 'Grand livre clients'), margin, 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(
      `${t('bonLivraison.client', 'Client')}: ${selectedClientLabel}  |  ${t('common.from', 'Du')} ${filters.dateFrom} ${t('common.to', 'Au')} ${filters.dateTo}`,
      margin,
      26
    );

    if (company?.name) {
      doc.text(company.name, pageWidth - margin, 18, { align: 'right' });
    }

    autoTable(doc, {
      startY: 32,
      head: [[
        t('common.date', 'Date'),
        t('common.type', 'Type'),
        t('common.reference', 'Reference'),
        t('common.description', 'Libelle'),
        t('common.debit', 'Debit'),
        t('common.credit', 'Credit'),
        t('common.balanceAfter', 'Solde'),
      ]],
      body: rows.map((row) => [
        formatDateFr(row.date),
        typeLabel(row.type),
        row.numero,
        row.libelle,
        row.debit ? formatPrice(row.debit, false) : '-',
        row.credit ? formatPrice(row.credit, false) : '-',
        formatSignedBalance(row.balance),
      ]),
      foot: [[
        '',
        '',
        '',
        t('common.totals', 'Totaux'),
        formatPrice(totals.totalDebit, false),
        formatPrice(totals.totalCredit, false),
        formatSignedBalance(totals.balance),
      ]],
      styles: { fontSize: 8.2, cellPadding: 2.3, overflow: 'linebreak' },
      headStyles: { fillColor: [233, 242, 255], textColor: [15, 23, 42], fontStyle: 'bold' },
      footStyles: { fillColor: [245, 245, 245], textColor: [15, 23, 42], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 30 },
        2: { cellWidth: 34 },
        3: { cellWidth: 90 },
        4: { cellWidth: 26, halign: 'right' },
        5: { cellWidth: 26, halign: 'right' },
        6: { cellWidth: 26, halign: 'right' },
      },
      margin: { left: margin, right: margin, bottom: 16 },
    });

    doc.save(`Grand_Livre_Clients_${filters.dateFrom}_${filters.dateTo}.pdf`);
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                <FaBook className="text-primary me-2" />
                {t('sales.grandLivreClient', 'Grand livre clients')}
              </h4>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm mb-4">
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label>{t('bonLivraison.client', 'Client')}</Label>
                <Select
                  options={clientOptions}
                  value={clientOptions.find((option) => option.value === filters.clientId)}
                  onChange={(option) => setFilters((prev) => ({ ...prev, clientId: option?.value ?? null }))}
                  isSearchable
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label>{t('common.month', 'Mois')}</Label>
                <Input
                  type="month"
                  value={filters.month}
                  onChange={(e) => {
                    const month = e.target.value;
                    const range = getMonthRange(month);
                    setFilters((prev) => ({ ...prev, month, dateFrom: range.dateFrom, dateTo: range.dateTo }));
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label>{t('common.from', 'Du')}</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((prev) => ({ ...prev, month: '', dateFrom: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label>{t('common.to', 'Au')}</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters((prev) => ({ ...prev, month: '', dateTo: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label>{t('reglement.search', 'Recherche')}</Label>
                <Input
                  type="text"
                  value={filters.query}
                  onChange={(e) => setFilters((prev) => ({ ...prev, query: e.target.value }))}
                  placeholder={t('common.search', 'Rechercher')}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <div className="d-flex gap-2">
                <Button color="primary" onClick={fetchLedger}>
                  <FaSearch />
                </Button>
                <Button color="success" onClick={handleExportExcel} disabled={!rows.length} title="Exporter Excel">
                  <FaFileExcel />
                </Button>
                <Button color="danger" onClick={handleExportPdf} disabled={!rows.length} title="Exporter PDF">
                  <FaFilePdf />
                </Button>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="shadow-sm">
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">{t('common.loading')}</div>
            </div>
          ) : error ? (
            <Alert color="danger">{error}</Alert>
          ) : (
            <div className="d-flex flex-column gap-4">
              {monthlySections.length === 0 ? (
                <div className="text-center text-muted py-4">
                  {t('common.noData', 'Aucune donnee')}
                </div>
              ) : monthlySections.map((section) => (
                <Card key={section.monthKey} className="border-0 shadow-sm bg-light bg-opacity-25">
                  <CardBody>
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1 text-capitalize">{section.monthLabel}</h5>
                        <small className="text-muted">Presentation type releve bancaire</small>
                      </div>
                      <div className="d-flex flex-wrap gap-3">
                        <div className="text-end">
                          <div className="small text-muted">Solde debut</div>
                          <div className={`fw-bold ${balanceTextClass(section.openingBalance)}`}>
                            {formatSignedBalance(section.openingBalance)}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="small text-muted">Debit du mois</div>
                          <div className="fw-bold text-danger">{formatPrice(section.totalDebit, false)}</div>
                        </div>
                        <div className="text-end">
                          <div className="small text-muted">Credit du mois</div>
                          <div className="fw-bold text-success">{formatPrice(section.totalCredit, false)}</div>
                        </div>
                        <div className="text-end">
                          <div className="small text-muted">Solde fin</div>
                          <div className={`fw-bold ${balanceTextClass(section.closingBalance)}`}>
                            {formatSignedBalance(section.closingBalance)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <Table striped hover bordered className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center">{t('common.date', 'Date')}</th>
                            <th className="text-center">{t('common.client', 'Client')}</th>
                            <th className="text-center">{t('common.type', 'Type')}</th>
                            <th className="text-center">{t('common.reference', 'Reference')}</th>
                            <th>{t('common.description', 'Libelle')}</th>
                            <th className="text-end">{t('common.debit', 'Debit')}</th>
                            <th className="text-end">{t('common.credit', 'Credit')}</th>
                            <th className="text-end">{t('common.balanceAfter', 'Solde')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="table-secondary">
                            <td colSpan="7" className="fw-bold">Solde debut de mois</td>
                            <td className={`text-end fw-bold ${balanceTextClass(section.openingBalance)}`}>
                              {formatSignedBalance(section.openingBalance)}
                            </td>
                          </tr>
                          {section.rows.map((row) => {
                            const Icon = row.icon;
                            return (
                              <tr key={row.id}>
                                <td className="text-center">{formatDateFr(row.date)}</td>
                                <td className="text-center">{row.client}</td>
                                <td className="text-center">
                                  <span className="d-inline-flex align-items-center gap-2">
                                    <Icon />
                                    {typeLabel(row.type)}
                                  </span>
                                </td>
                                <td className="text-center">{row.numero}</td>
                                <td>{row.libelle}</td>
                                <td className="text-end">{row.debit ? formatPrice(row.debit, false) : '-'}</td>
                                <td className="text-end">{row.credit ? formatPrice(row.credit, false) : '-'}</td>
                                <td className={`text-end fw-bold ${balanceTextClass(row.balance)}`}>
                                  {formatSignedBalance(row.balance)}
                                </td>
                              </tr>
                            );
                          })}
                          <tr className="table-light">
                            <td colSpan="5" className="fw-bold">Totaux du mois</td>
                            <td className="text-end fw-bold">{formatPrice(section.totalDebit, false)}</td>
                            <td className="text-end fw-bold">{formatPrice(section.totalCredit, false)}</td>
                            <td></td>
                          </tr>
                          <tr className="table-primary">
                            <td colSpan="7" className="fw-bold">Solde fin de mois</td>
                            <td className={`text-end fw-bold ${balanceTextClass(section.closingBalance)}`}>
                              {formatSignedBalance(section.closingBalance)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default GrandLivreClientPage;
